<template>
  <card id="gatewayConfigurationAlarms" :title="$t('gc-alarms:alarms')">
    <div slot="card-body">
      <div class="row">
        <div v-for="(alarmsField, index) in alarmsFields" :key="index" class="col-12 col-xl-6">
          <div class="form-row">
            <h3 class="col-12">{{ $t('gc-alarms:alarmsList.' + alarmsField.name) | capitalize }}</h3>

            <div class="form-group col-sm-4 col-xl-3">
              <label class="control-label">{{ $t('gc-alarms:currentValue') | capitalize }}</label>
              <div :class="[getColor(alarmsField)]" class="form-control border-0 pl-2">
                {{ alarms[alarmsField.current] }} <template v-if="!alarmsField.on">{{ alarmsField.unit }}</template>
              </div>
            </div>

            <div class="form-group col-sm-4">
              <label :for="alarmsField.threshold" class="control-label">{{ $t('gc-alarms:threshold') | capitalize }}</label>
              <input :id="alarmsField.threshold"
                     v-model="alarms[alarmsField.threshold]"
                     v-validate="'required|between:' + alarmsField.min + ',' + alarmsField.max + ''"
                     :name="alarmsField.threshold"
                     :disabled="disabled"
                     :min="alarmsField.min"
                     :max="alarmsField.max"
                     :step="alarmsField.step || 1"
                     :class="{ 'is-invalid': errors.has(alarmsField.threshold) }"
                     :data-vv-as="$t('gc-alarms:threshold')"
                     type="text"
                     class="form-control"
              >
              <small class="form-text text-muted">{{ $t('gc-alarms:thresholdUnit', { unit: alarmsField.unit }) | capitalize }}</small>
              <div class="invalid-feedback">{{ errors.first(alarmsField.threshold) }}</div>
            </div>

            <div v-if="alarmsField.hysteresis" class="form-group col-sm-4">
              <label :for="alarmsField.hysteresis" class="control-label">{{ $t('gc-alarms:hysteresis') | capitalize }}</label>
              <input :id="alarmsField.hysteresis"
                     v-model="alarms[alarmsField.hysteresis]"
                     v-validate="'required|between:' + (alarmsField.hmin !== undefined ? alarmsField.hmin : alarmsField.min) + ',' + (alarmsField.hmax !== undefined ? alarmsField.hmax : alarmsField.max) + ''"
                     :name="alarmsField.hysteresis"
                     :disabled="disabled"
                     :min="alarmsField.hmin !== undefined ? alarmsField.hmin : alarmsField.min"
                     :max="alarmsField.hmax !== undefined ? alarmsField.hmax : alarmsField.max"
                     :step="alarmsField.hstep !== undefined ? alarmsField.hstep : alarmsField.step || 1"
                     :class="{ 'is-invalid': errors.has(alarmsField.hysteresis) }"
                     :data-vv-as="$t('gc-alarms:hysteresis')"
                     type="text"
                     class="form-control"
              >
              <small class="form-text text-muted">{{ $t('gc-alarms:hysteresisUnit', { unit: alarmsField.unit }) | capitalize }}</small>
              <div class="invalid-feedback">{{ errors.first(alarmsField.hysteresis) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div slot="card-footer">
      <button id="sendButton" :disabled="disabled" class="btn btn-primary" @click="send">{{ $t('gc-alarms:send') }}</button>
    </div>
  </card>
</template>

<script>
import { isEmpty, pick } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

export default {
  titleKey: 'gatewayConfAlarms',

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      alarms: {},
      getting: false,
      sending: false
    }
  },

  computed: {
    ...mapGetters(['gateway']),

    alarmsFields () {
      const alarmsFields = [{
        name: 'cpu',
        current: 'cpuCurrent',
        threshold: 'cpuAlarmThreshold',
        hysteresis: 'cpuAlarmHysteresis',
        unit: '%',
        min: 0,
        max: 100
      }, {
        name: 'ram',
        current: 'ramCurrent',
        threshold: 'ramAlarmThreshold',
        hysteresis: 'ramAlarmHysteresis',
        unit: '%',
        min: 0,
        max: 100
      }, {
        name: 'systemDisk',
        current: 'diskSystemVolumeCurrent',
        threshold: 'diskSystemAlarmThreshold',
        hysteresis: 'diskSystemAlarmHysteresis',
        unit: '%',
        min: 0,
        max: 100
      }, {
        name: 'userDisk',
        current: 'diskUserVolumeCurrent',
        threshold: 'diskUserAlarmThreshold',
        hysteresis: 'diskUserAlarmHysteresis',
        unit: '%',
        min: 0,
        max: 100
      }, {
        name: 'rssi',
        current: 'rssiCurrent',
        threshold: 'rssiAlarmThreshold',
        hysteresis: 'rssiAlarmHysteresis',
        unit: 'dBm',
        min: -120,
        max: -50,
        hmin: 0,
        hmax: 50
      }, {
        name: 'temperature',
        current: 'temperatureCurrent',
        threshold: 'temperatureAlarmThreshold',
        hysteresis: 'temperatureAlarmHysteresis',
        unit: '°C',
        min: 0,
        max: 90
      }, {
        name: 'door',
        current: 'doorStateCurrent',
        threshold: 'doorThreshold',
        unit: 'lx',
        min: 0,
        max: 80000,
        step: 1000,
        on: 'CLOSED',
        off: 'OPEN'
      }]

      if (this.$store.getters.gatewayFeature(this.gateway, 'gps')) {
        alarmsFields.push({
          name: 'gps',
          current: 'gpsStatusCurrent',
          threshold: 'gpsThreshold',
          unit: this.$t('gc-alarms:second'),
          min: 0,
          max: 3600,
          on: 'LOCKED',
          off: 'UNLOCKED'
        })
      }

      return alarmsFields
    },

    disabled () {
      return isEmpty(this.alarms) || this.sending
    }
  },

  watch: {
    eui () {
      // Reset data, and get data from new gateway
      this.alarms = {}
    },

    gateway () {
      // Do not refresh is we olready have the data
      if (!isEmpty(this.alarms) || this.getting) return

      this.getAlarms()
    }
  },

  created () {
    this.getAlarms()
  },

  methods: {
    async getAlarms () {
      if (isEmpty(this.gateway)) return

      if (!this.gateway.connection) {
        eventBus.$emit('message-info', this.$t('gc-alarms:gatewayNotConnected'))
        return
      }

      this.getting = true
      let error, response
      [error, response] = await to(this.$api.getGatewayControl(this.gateway))
      if (!error && response) this.alarms = response.data

      this.getting = false
    },

    getColor (alarmsField) {
      if (this.disabled) {
        return 'text-muted'
      } else if (alarmsField.on) {
        return this.alarms[alarmsField.current] === alarmsField.on
          ? 'text-success'
          : this.alarms[alarmsField.current] === alarmsField.off
            ? 'text-danger'
            : 'text-muted'
      } else {
        // RSSI in negative, invert colors
        if (alarmsField.name === 'rssi') {
          return this.alarms[alarmsField.current] <= this.alarms[alarmsField.threshold]
            ? this.alarms[alarmsField.current] <= this.alarms[alarmsField.threshold] - this.alarms[alarmsField.hysteresis]
              ? 'text-danger'
              : 'text-warning'
            : 'text-success'
        }

        return this.alarms[alarmsField.current] >= this.alarms[alarmsField.threshold] - this.alarms[alarmsField.hysteresis]
          ? this.alarms[alarmsField.current] >= this.alarms[alarmsField.threshold]
            ? 'text-danger'
            : 'text-warning'
          : 'text-success'
      }
    },

    async send () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      this.sending = true

      // only keep threshold and hysteresis values
      let pickedValues = []
      for (let i = 0; i < this.alarmsFields.length; i++) {
        if (this.alarmsFields[i].threshold) {
          pickedValues.push(this.alarmsFields[i].threshold)
        }
        if (this.alarmsFields[i].hysteresis) {
          pickedValues.push(this.alarmsFields[i].hysteresis)
        }
      }

      let error
      [error] = await to(this.$api.updateGatewayControl(this.gateway, pick(this.alarms, pickedValues)))
      if (!error) eventBus.$emit('message-success', this.$t('gc-alarms:sendSuccess'))

      this.sending = false
    }
  }
}
</script>
